<template>
  <div class="games">
    <q-inner-loading :showing="visible" class="fullscreen">
      <q-spinner size="50px" color="secondary" />
    </q-inner-loading>
    <section class="games-filter">
      <div class="games-filter__categories">
        <q-btn
          :color="this.selectedCategoryName === 'All' ? 'secondary' : 'white'"
          :class="
            this.selectedCategoryName === 'All' ? 'text-white' : 'text-black'
          "
          rounded
          unelevated
          label="All"
          class="q-mr-sm"
          @click="updateGameCategoryView()"
        />
        <span v-for="(category, index) in categories" :key="index">
          <q-btn
            v-if="category.game_infos.length > 0"
            @click="updateGameCategoryView(category)"
            rounded
            unelevated
            :color="
              selectedCategoryName === category.CategoryName
                ? 'secondary'
                : 'white'
            "
            :class="
              selectedCategoryName === category.CategoryName
                ? 'text-white'
                : 'text-black'
            "
            class="q-mr-sm"
            :label="category.CategoryName"
          />
        </span>
      </div>
      <div class="games-filter__queries">
        <!-- Search -->
        <q-input
          outlined
          v-model="search"
          label="Search Games"
          class="q-mr-md"
          style="min-width: 250px"
          debounce="500"
          @clear="resetSearch()"
          clearable
          @blur="setSearch()"
        />
        <q-select
          color="secondary"
          outlined
          multiple
          v-model="selectedProviders"
          :options="this.providers"
          option-value="id"
          option-label="ProviderName"
          map-options
          emit-value
          label="Game Providers"
          style="min-width: 250px"
          @clear="resetProviders()"
          clearable
          @input="setProvider()"
        />
      </div>
    </section>
    <section class="games-category">
      <div class="games-category__count">
        Games: <span>{{ filteredGames.length }}</span>
      </div>
      <q-intersection v-for="(game, index) in filteredGames" :key="index">
        <div class="games-card">
          <div class="games-card__image">
            <q-img
              v-if="game.GameImage"
              :src="game.GameImage.url"
              :alt="game.GameImage.alternativeText"
              class="games-card__image--height"
              contain
            />
            <div
              class="games-card__image--height games-card__image--no-image"
              v-else
            >
              <q-icon size="lg" name="broken_image" class="q-mb-sm" />
              <span>No image available</span>
            </div>
            <div class="actions">
              <q-btn
                class="q-ma-sm"
                color="secondary"
                unelevated
                label="Read More"
                @click="openGameItem(game.id, game.GameName)"
                no-caps
              />
              <q-btn
                class="q-ma-sm"
                :disable="!game.resolvedDemoUrl"
                :color="game.resolvedDemoUrl ? 'secondary' : 'grey'"
                unelevated
                outline
                label="Demo"
                @click="
                  openDemo(
                    game.resolvedDemoUrl,
                    game.openDemoInIframe,
                    game.GameName
                  )
                "
                no-caps
              />
            </div>
          </div>
          <div class="games-card__content">
            <div class="games-card__title">
              <h2>{{ game.GameName }}<sup>TM</sup></h2>
            </div>
            <div class="games-card-table">
              <div class="games-card-table__column">
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Release Date</span
                  ><span class="games-card-table__value">{{
                    formatDate(game.ReleaseDate)
                  }}</span>
                </div>

                <div class="games-card-table__row">
                  <span class="games-card-table__label">Default Bet</span
                  ><span class="games-card-table__value">{{
                    formatCurrencyDecimal(game.DefBet)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Minimum Total Bet</span
                  ><span class="games-card-table__value">{{
                    formatCurrencyDecimal(game.MinBet)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Maximum Total Bet</span
                  ><span class="games-card-table__value">{{
                    formatCurrencyDecimal(game.MaxBet)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Paylines</span
                  ><span class="games-card-table__value">{{ game.Lines }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Max Multiplier</span
                  ><span class="games-card-table__value">{{
                    formatStringCurrencyDecimal(game.Multiplier)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Max Exposure</span
                  ><span class="games-card-table__value">{{
                    formatCurrencyDecimal(game.MaxExposure)
                  }}</span>
                </div>
              </div>
              <div
                class="games-card-table__column games-card-table__column--hide-mobile"
              >
                <div class="games-card-table__row">
                  <span class="games-card-table__label">RTP</span
                  ><span class="games-card-table__value">{{ game.RTP }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Free Spins</span
                  ><span class="games-card-table__value">{{
                    trueFalse(game.FreeSpins)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Volatility</span
                  ><span class="games-card-table__value">{{
                    formatTextLabel(game.Volatility)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Bouns Buy</span
                  ><span class="games-card-table__value">{{
                    trueFalse(game.BonusBuy)
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Aggregator</span
                  ><span class="games-card-table__value">{{
                    game.aggregator.Name
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Provider</span
                  ><span class="games-card-table__value">{{
                    getProvider(game.game_provider)
                    
                  }}</span>
                </div>
                <div class="games-card-table__row">
                  <span class="games-card-table__label">Game Layout</span
                  ><span class="games-card-table__value">{{
                    game.GameLayout
                  }}</span>
                </div>
              </div>
            </div>
            <div class="games-card__footer">
              <div class="actions">
                <q-btn
                  class="q-ma-sm"
                  color="secondary"
                  unelevated
                  label="Read More"
                  @click="openGameItem(game.id, game.GameName)"
                />
                <q-btn
                  class="q-ma-sm"
                  :disable="!game.resolvedDemoUrl"
                  :color="game.resolvedDemoUrl ? 'secondary' : 'grey'"
                  unelevated
                  outline
                  label="Demo"
                  @click="
                    openDemo(
                      game.resolvedDemoUrl,
                      game.openDemoInIframe,
                      game.GameName
                    )
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </q-intersection>
    </section>
    <IframeDialog :open.sync="dialogOpen" :url="url" :title="dialogTitle" />
  </div>
</template>

<script>
import router from "@/router";
import { formatDate } from "@/utils/formatDate";
import {
  generateDemoGameUrl,
  matchProviderUrlToGame,
} from "@/utils/formatGameUrl";
import {
  formatStringCurrencyDecimal,
  formatCurrencyDecimal,
} from "@/utils/formatCurrency";
import { formatTextLabel, formatUrl } from "@/utils/formatText";
import { trueFalse } from "@/utils/formatBoolean";
import IframeDialog from "@/components/dialog/Iframe.vue";
import { gameDemoInIframeMatchProvider } from "./AllGamesNew/AllGames.utils";
export default {
  name: "AllGames",
  components: { IframeDialog },
  data() {
    return {
      categories: [],
      selectedCategoryName: "All",
      categoryId: [],
      filterList: [],
      visible: false,
      search: null,
      selectedProviders: [],
      providers: [],
      aggregators: [],
      dialogOpen: false,
      dialogTitle: "",
      url: "",
    };
  },
  methods: {
    generateDemoGameUrl,
    matchProviderUrlToGame,
    formatDate,
    formatStringCurrencyDecimal,
    formatCurrencyDecimal,
    formatTextLabel,
    trueFalse,
    gameDemoInIframeMatchProvider,
    async fetchAllGameCategories() {
      this.visible = true;

      const payload = {
        link: "game-categories",
        params: {
          populate: {
            game_infos: {
              populate: 'aggregator', 
            }
          }
        }
      };
      await this.$store
        .dispatch("fetchStrapiContent", payload)
        .then((response) => {
          if (response !== undefined) {
        
            this.categories = response.data;
            this.filterList = this.categories;
            this.visible = false;
          }
        })
        .catch((error) => {
          this.errors.push(error);
        });
    },

    async fetchGameProviders() {
      const payload = {
        link: "game-providers",
      };
      await this.$store
        .dispatch("fetchStrapiContent", payload)
        .then((response) => {
          if (response !== undefined && response.data !== undefined) {
            this.providers = response.data.data
              .map(provider => ({
                id: provider.id,
                ProviderName: provider.attributes.ProviderName,
                // include any other attributes you need
                ...provider.attributes
              }))
              .sort((a, b) => {
                // Compare the ProviderName of two providers
                if (a.ProviderName.toLowerCase() < b.ProviderName.toLowerCase()) {
                  return -1;
                }
                if (a.ProviderName.toLowerCase() > b.ProviderName.toLowerCase()) {
                  return 1;
                }
                return 0;
              });
          }
        })
        .catch((error) => {
          this.errors.push(error);
        });
    },
    async fetchAggregators() {
      const payload = {
        link: "aggregators",
        params: {
          populate: "game_infos",
        }
        
      };
      await this.$store
        .dispatch("fetchStrapiContent", payload)
        .then((response) => {
          if (response !== undefined) {
            // Put all Aggregators into an array
            this.aggregators = response.data.data;
          }
        })
        .catch((error) => {
          this.errors.push(error);
        });
    },

    updateGameCategoryView(category) {
      this.visible = true;
      // When category selected from filter - only present that category on the page
      if (category) {
        // Pass selected category into a new array and update model
        this.$store.commit("SET_CATEGORY", {
          selectedCategoryName: category.CategoryName,
          categoryId: category.id,
        });
        this.selectedCategoryName = category.CategoryName;
        this.categoryId = parseInt(category.id);
        this.filterList = Array(category);
        this.visible = false;
      } else {
        // If user clicks All button - revert to orginally fetched categories
        this.$store.commit("SET_CATEGORY", {
          selectedCategoryName: "All",
          categoryId: null,
        });
        this.selectedCategoryName = "All";
        this.categoryId = null;
        this.filterList = this.categories;
        this.visible = false;
      }
    },

    openGameItem(id, gameName) {
      // format game name for url
      const name = formatUrl(gameName);
      // Push game id and name into the SingleGame component and render the new page
      router.push({
        name: "Game",
        params: { id, name },
        query: { name: name },
      });
    },

    openDemo(url, demoIframeEnabled, name) {
      if (!demoIframeEnabled) {
        window.open(url, "_blank");
      }
      if (demoIframeEnabled) {
        this.dialogOpen = true;
        this.dialogTitle = name + " Demo";
        this.url = url;
      }
    },

    setSearch() {
      this.$store.commit("SET_SEARCH_QUERY", {
        searchQuery: this.search,
      });
    },

    resetSearch() {
      this.search = null;
      this.$store.commit("SET_SEARCH_QUERY", {
        searchQuery: null,
      });
    },

    setProvider() {
      this.$store.commit("SET_PROVIDERS", {
        selectedProviders: this.selectedProviders,
      });
    },

    resetProviders() {
      this.selectedProviders = [];
      this.$store.commit("SET_PROVIDERS", {
        selectedProviders: [],
      });
    },
    getProvider(value) {
      if (value !== null && value !== undefined && value !== '' && value.ProviderName !== null && value.ProviderName !== undefined && value.ProviderName !== '' ) {
        return value.ProviderName;
      }
      else {
        return "";
      }
    },
  },
  mounted() {
    // Providers and Aggregators has to be fetched before Game Categories otherwise execution errors appear for getProvider
    this.fetchGameProviders();
    this.fetchAggregators();
    this.fetchAllGameCategories();

    // If already filtered - fetch from store
    this.selectedCategoryName = this.$store.getters.getSelectedCategoryName
      ? this.$store.getters.getSelectedCategoryName
      : "All";
    this.categoryId = this.$store.getters.getCategoryId
      ? this.$store.getters.getCategoryId
      : null;
    this.selectedProviders = this.$store.getters.getSelectedProviders
      ? this.$store.getters.getSelectedProviders
      : [];
    this.search = this.$store.getters.getSearchQuery
      ? this.$store.getters.getSearchQuery
      : null;
  },
  computed: {
    // Put all games into an array and apply filters
    filteredGames() {
      let gamesList = [];

      this.filterList.forEach((category) => {
        this.visible = true;
        // 1. SINGLE ARGUMENTS
        // --- If a games category selected
        if (
          this.categoryId !== null &&
          this.search === null &&
          this.selectedProviders.length < 1
        ) {
          console.log("If a games category selected");
          
          // Ensure categories array is defined and not empty
          if (Array.isArray(this.categories) && this.categories.length > 0) {
            const categoryFound = this.categories.find(cat => cat.id === this.categoryId);

            if (categoryFound && Array.isArray(categoryFound.game_infos)) {
              categoryFound.game_infos.forEach((game) => {
                // Check if the game already exists in gamesList
                const exists = gamesList.some(existingGame => existingGame.id === game.id);

                if (!exists) {
                  gamesList.push(game);
                }
              });
              this.visible = false;
            } 
          }
        }
        // --- If a search query
        else if (
          this.categoryId === null &&
          this.search !== null &&
          this.selectedProviders.length < 1
        ) {
          console.log("If a search query");
          category.game_infos
            .filter((game) => {
              return game.GameName.toLowerCase().includes(
                this.search.toLowerCase()
              );
            })
            .forEach((game) => {
              gamesList.push(game);
            });
          this.visible = false;
        }
        // --- If a game provider selected
        else if (
          this.categoryId === null &&
          this.search === null &&
          this.selectedProviders.length >= 1
        ) {
          console.log("if a game provider selected");
          category.game_infos
            .filter((game) => {
                return game.game_provider && this.selectedProviders.includes(game.game_provider.id);
            })
            .forEach((game) => {
              gamesList.push(game);
            });
            
          this.visible = false;
        }
        // 2. TWO ARGUMENTS
        // --- If a games category selected and a search query
        else if (
          this.categoryId !== null &&
          this.search !== null &&
          this.selectedProviders.length < 1
        ) {
          console.log("If a games category selected and a search query");
          if (Array.isArray(this.categories) && this.categories.length > 0) {
            const categoryFound = this.categories.find(cat => cat.id === this.categoryId);

            if (categoryFound && Array.isArray(categoryFound.game_infos)) {
              categoryFound.game_infos.forEach((game) => {
                // Check if the game already exists in gamesList and if it matches search term
                const exists = gamesList.some(existingGame => existingGame.id === game.id);
                const matchesSearch = game.GameName.toLowerCase().includes(this.search.toLowerCase());
                if (!exists && matchesSearch) {
                  gamesList.push(game);
                }
              });
              this.visible = false;
            } 
          }
        }
        // --- If a games category and a game provider selected
        else if (
          this.categoryId !== null &&
          this.search === null &&
          this.selectedProviders.length >= 1
        ) {
          console.log("If a games category and a game provider selected");

          if (Array.isArray(this.categories) && this.categories.length > 0) {
            const categoryFound = this.categories.find(cat => cat.id === this.categoryId);

            if (categoryFound && Array.isArray(categoryFound.game_infos)) {
              categoryFound.game_infos.forEach((game) => {
                // Check if the game already exists in gamesList and if it matches search term
                const exists = gamesList.some(existingGame => existingGame.id === game.id);
                const matchesProvider = game.game_provider && this.selectedProviders.includes(game.game_provider.id);
                if (!exists && matchesProvider) {
                  gamesList.push(game);
                }
              });
              this.visible = false;
            } 
          }
        }
        // --- If a game provider selected and search query
        else if (
          this.categoryId === null &&
          this.search !== null &&
          this.selectedProviders.length >= 1
        ) {
          console.log("If a game provider selected and search query");
          category.game_infos
            .filter((game) => {
              return (
                game.game_provider && 
                this.selectedProviders.includes(game.game_provider.id) &&
                game.GameName.toLowerCase().includes(this.search.toLowerCase())
              );
            })
            .forEach((game) => {
              gamesList.push(game);
            });
          this.visible = false;
        }
        // 3. THREE ARGUMENTS
        // --- If a games category selected, a search query and game provider selected
        else if (
          this.categoryId !== null &&
          this.search !== null &&
          this.selectedProviders.length >= 1
        ) {
          console.log("If a games category selected, a search query and game provider selected");

          if (Array.isArray(this.categories) && this.categories.length > 0) {
            const categoryFound = this.categories.find(cat => cat.id === this.categoryId);

            if (categoryFound && Array.isArray(categoryFound.game_infos)) {
              categoryFound.game_infos.forEach((game) => {
                // Check if the game already exists in gamesList and if it matches search term
                const exists = gamesList.some(existingGame => existingGame.id === game.id);
                const matchesProvider = game.game_provider && this.selectedProviders.includes(game.game_provider.id);
                const matchesSearch = game.GameName.toLowerCase().includes(this.search.toLowerCase());
                if (!exists && matchesProvider && matchesSearch) {
                  gamesList.push(game);
                }
              });
              this.visible = false;
            } 
          }

          category.game_infos
            .filter((game) => {
              return (
                game.game_provider && 
                this.selectedProviders.includes(game.game_provider.id) &&
                game.game_category === this.categoryId &&
                game.GameName.toLowerCase().includes(this.search.toLowerCase())
              );
            })
            .forEach((game) => {
              gamesList.push(game);
            });
          this.visible = false;
        }
        // NO ARGUMENTS
        else {
          console.log("no arguments");
          category.game_infos.forEach((game) => {
            gamesList.push(game);
          });
          this.visible = false;
        }
      });

      return gamesList.map((game) => {
        const gameProviderUrl = this.getProviderUrl(game.game_provider);
        return {
          ...game,
          resolvedDemoUrl: this.generateDemoGameUrl(
            gameProviderUrl,
            game.GameDemoLink
          ),
          openDemoInIframe: this.getGameDemoIframe(game.game_provider),
        };
      });
    },
    getProviderUrl() {
      return matchProviderUrlToGame(this.providers);
    },
    getGameDemoIframe() {
      return gameDemoInIframeMatchProvider(this.providers);
    },
  },
};
</script>